import { useAuth0 } from '@auth0/auth0-react';
import React, {
  DependencyList,
  Dispatch,
  EffectCallback,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cacheFactory, getTrigger } from '@shared/helpers/function.helpers';
import shallowEqual from '@shared/helpers/array.helpers';
import { isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

/**
 * Generate a unique ID for a component. Use if you need to pass along a component ID somewhere.
 * The UUID is generated on mount and will never change (unless the component is remounted, for example if another key is passed to the component).
 * @returns A v4 UUID
 */
export const useComponentUUID = (): string => {
  return useMemo(() => uuidv4(), []);
};

/**
 * Debounce a value
 * @param value Value to debounce
 * @param delay - Optional delay in milliseconds. If undefined, the value will be returned immediately.
 * @returns
 */
export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    if (typeof delay === 'number') {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }
  }, [delay, value]);

  // If undefined delay don't debounce (0 will still debounce 0ms and thus trigger outside of scope)
  if (typeof delay !== 'number') {
    setDebouncedValue(value);
    return value; // No delay
  }

  return debouncedValue;
}

/**
 * Debounce the call of a function.
 * When delay has passed, the function will be called once with the latest arguments.
 * Each time the returned function is called, the delay will be reset.
 *
 * @param value Function to debounce
 * @param delay - Delay in milliseconds.
 * @returns Debounced function. The function is always stable
 */
export function useDebouncedFunction<T extends unknown[]>(
  func: (...args: T) => void,
  delay: number,
): (...args: T) => void {
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const delayRef = useRef(delay);
  useEffect(() => {
    delayRef.current = delay;
  }, [delay]);

  // make sure it's always the latest function that get's called
  const functionRef = useRef(func);
  useEffect(() => {
    functionRef.current = func;
  }, [func]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return useCallback((...args: T) => {
    clearTimeout(timeoutRef.current);

    if (delayRef.current === 0) functionRef.current(...args);
    else {
      timeoutRef.current = setTimeout(
        () => functionRef.current(...args),
        delayRef.current,
      );
    }
  }, []);
}

export const useToggleState = (
  initialState = false,
  [on, off] = [true, false],
): [boolean, () => void, Dispatch<React.SetStateAction<boolean>>] => {
  const [state, setState] = useState(initialState);

  const toggleState = useCallback(() => {
    setState((s) => (s === on ? off : on));
  }, [on, off]);

  return [state, toggleState, setState];
};

/**
 * A hook that manages a boolean state and provides functions to set the state to true and false.
 * @param initialState - The initial state of the boolean
 * @returns [state, setTrue, setFalse] - The current state, a function to set the state to true, and a function to set the state to false
 */
export const useBooleanState = (
  initialState = false,
): [boolean, setTrue: () => void, setFalse: () => void] => {
  const [state, setState] = useState(initialState);

  const setTrue = useCallback(() => setState(true), []);
  const setFalse = useCallback(() => setState(false), []);

  return [state, setTrue, setFalse];
};

type GetTrigger = typeof getTrigger;

/**
 * Get trigger function that calls provided function with provided arguments
 * Good to use for event handlers like onClick etc
 */
export const useTrigger: GetTrigger = (
  ...args: Parameters<GetTrigger>
): ReturnType<GetTrigger> => {
  // Can't do ESLInts recommendation here because [args] would be a new item every time
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const cb = useCallback(getTrigger(...args), args);
  return cb;
};

export const useLogoutIfUnauthorized = (): ((error: any) => void) => {
  const { logout } = useAuth0();

  return useCallback(
    (error: any) => {
      if (error?.response?.status === 401) {
        logout();
      }
    },
    [logout],
  );
};

/**
 * Trigger new calculation of value when dependencies change like useMemo
 * but use deep comparison to check if dependencies have changed instead of shallow comparison.
 * Avoid sending in large objects like project or version as dependencies for performance reasons.
 * @param factory
 * @param deps
 * @returns
 */
export function useMemoDeepEqual<T>(factory: () => T, deps: DependencyList): T {
  const prevDepsRef = useRef<DependencyList | undefined>(undefined);
  const factoryResultRef = useRef<T | undefined>(undefined);

  const prevDeps = prevDepsRef.current;

  if (deps === undefined) {
    throw new Error('Undefined DependencyList');
  }

  // Run factory function first time or if deps have changed
  if (prevDeps === undefined || !isEqual(prevDeps, deps)) {
    prevDepsRef.current = deps;
    factoryResultRef.current = factory();
  }

  // It should only be undefined if factory returns undefined so cast to T
  return factoryResultRef.current as T;
}

type UsePreviousOptions<T> = {
  /** When true, the initial value will be set to {@link value} instead of undefined. */
  setInitial?: boolean;
  /** Only update the stored value when this predicate returns true. */
  when?: (value: T) => boolean;
};

/**
 * Keep track of previous value of a variable.
 * @param value
 * @param options
 * @returns The previous value of the variable
 */
export function usePrevious<T>(
  value: T,
  options: UsePreviousOptions<T> & { setInitial: true },
): T;
export function usePrevious<T>(
  value: T,
  options?: UsePreviousOptions<T>,
): T | undefined;
export function usePrevious<T>(
  value: T,
  options?: UsePreviousOptions<T>,
): T | undefined {
  const ref = useRef<T | undefined>(options?.setInitial ? value : undefined);
  const previous = ref.current;
  if (options?.when?.(value) !== false) ref.current = value;
  return previous;
}

export const useHasChanged = (val: unknown): boolean =>
  usePrevious(val) !== val;

type ShallowCompareInput = Parameters<typeof shallowEqual>[0];

/**
 * Check if a value has changed using shallow comparison
 * @param val
 * @returns
 */
export const useHasChangedShallow = (val: ShallowCompareInput): boolean => {
  const prev = usePrevious(val);
  return !shallowEqual(prev, val);
};

/**
 * Check if a value has changed using deep comparison
 * @param val
 * @returns
 */
export const useHasChangedDeep = (val: unknown): boolean => {
  const prev = usePrevious(val);
  return !isEqual(prev, val);
};

export const useGranularEffect = (
  effect: EffectCallback,
  primaryDeps: DependencyList,
  secondaryDeps: DependencyList,
): void => {
  const ref = useRef<DependencyList | undefined>(undefined);
  const current = ref.current;

  // Run if any property in primary deps have changed
  if (!current || !primaryDeps.every((w, i) => Object.is(w, current[i]))) {
    ref.current = [...primaryDeps, ...secondaryDeps];
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useEffect(effect, ref.current);
};

// All types of "typeof variable" can be
type TypeOfTypes =
  | 'string'
  | 'number'
  | 'bigint'
  | 'boolean'
  | 'symbol'
  | 'undefined'
  | 'object'
  | 'function';

/**
 * Default shouldn't check for changed functions
 */
const effectOfTypesDefaults: TypeOfTypes[] = [
  'string',
  'number',
  'bigint',
  'boolean',
  'symbol',
  'undefined',
  'object',
];

export const useEffectOfTypes = (
  effect: EffectCallback,
  deps: DependencyList,
  types: TypeOfTypes[] = effectOfTypesDefaults,
): void => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useEffect(
    effect,
    deps.filter((d) => types.includes(typeof d)),
  );
};

/**
 * Memoize/cache the result of a factory function. Will only recompute the memoized value when one of the deps has changed (on that specific key).
 * @param factory Factory function to cache. Will only be called initially and if the deps
 * @param key Key used to invalidate the cache. Deps and value are stored used by this key. And if the key changes, the memoized value will be recomputed.
 * @param deps Dependencies to invalidate the cache by
 * @param options Options for the cache
 */
export const useSharedMemo = cacheFactory;

/**
 * Run a function only once when the component mounts (regardless of dependencies)
 * TODO: Add tests
 * @param func
 */
export function useOnce(func: () => any) {
  const funcRef = useRef(func);

  useEffect(() => {
    funcRef.current();
  }, []);
}

/**
 * A hook that mimics the useState hook but also exposes a ref with the latest value.
 * @param initialState Initial state
 * @returns [state, setState, ref] - The current state, the setState function, and the ref object
 */
export const useStateWithRef = <T>(
  initialState: T,
): [T, (value: T) => void, RefObject<T>] => {
  const [state, internalSetState] = useState(initialState);
  const ref = useRef(initialState);

  // Keep state and ref in sync and only update state if the value has changed
  const setState = useCallback((value: T) => {
    ref.current = value;
    internalSetState(value);
  }, []);

  return useMemo(() => [state, setState, ref], [state, setState]);
};
